<template>
  <div>
    <button class="btn btn-green" @click="showModal">
      + New contract
    </button>

    <!-- Modal -->
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Contract</h5>
          <button type="button" class="btn-close" @click="closeModal">X</button>
        </div>
        <div class="modal-body">
          <div class="background-white">
            <!-- Tab Navigation -->
            <ul class="nav nav-tabs">
              <li class="nav-item">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === 'general' }"
                  @click="activeTab = 'general'"
                >General</a>
              </li>
            </ul>

            <!-- General Tab -->
            <div v-if="activeTab === 'general'" class="tab-content">
              <div class="row align-items-start">
                <!-- 左邊：合約資訊 -->
                <div class="col-md-5 col-12 d-flex">
                  <div class="card flex-grow-1">
                    <div class="card-body">
                      <h5>Contract details</h5>
                      <div class="row">
                        <div class="col-12 p-0">
                          <label for="tid" class="form-label">TID</label>
                          <input
                            type="text"
                            class="form-control"
                            id="tid"
                            v-model="form.tid"
                          />
                          <div v-if="errors.tid" class="text-danger">{{ errors.tid }}</div>
                        </div>
                        <div class="col-md-6 col-12 p-0">
                          <label for="mid" class="form-label">MID</label>
                          <input
                            type="text"
                            class="form-control"
                            id="mid"
                            v-model="form.mid"
                          />
                          <div v-if="errors.mid" class="text-danger">{{ errors.mid }}</div>
                        </div>
                        <div class="col-md-6 col-12 p-0">
                          <label for="serial-number" class="form-label">Serial number</label>
                          <input
                            type="text"
                            class="form-control"
                            id="serial-number"
                            v-model="form.serial_number"
                          />
                          <div v-if="errors.serial_number" class="text-danger">{{ errors.serial_number }}</div>
                        </div>
                        <div class="col-12 mt-2 checkbox checkbox-success">
                          <input id="accessEnabled" type="checkbox" v-model="form.enabled" />
                          <label for="accessEnabled">Enabled</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- 右邊：派工資訊 -->
                <div class="col-md-7 col-12 d-flex">
                  <div class="card flex-grow-1">
                    <div class="card-body">
                      <h5>Assignment</h5>
                      <div class="row">
                        <div class="col-md-4 col-12">
                          <label for="driverID" class="form-label">Driver ID</label>
                          <input
                            type="text"
                            class="form-control"
                            id="driverID"
                            v-model="form.driver_id"
                            @input="clearDriverName"
                            @keyup.enter="fetchDriverInfo"
                            data-bs-toggle="tooltip"
                            title="Please press Enter after entering ID"
                          />
                          <div v-if="errors.driver_id" class="text-danger">{{ errors.driver_id }}</div>
                        </div>
                        <div class="col-md-8 col-12">
                          <label for="name" class="form-label">Name</label>
                          <input
                            type="text"
                            class="form-control"
                            id="name"
                            :value="driverName"
                            disabled
                          />
                        </div>
                        <!-- Fleet 區塊 -->
                        <div class="col-md-4 col-12"></div>
                        <div class="col-md-8 d-flex">
                          <div class="checkbox checkbox-success mt-4 d-flex align-items-center p-0">
                            <input
                              id="subgroup-checkbox"
                              type="checkbox"
                              class="form-check-input"
                              v-model="isFleetChecked"
                            />
                            <label for="subgroup-checkbox" class="form-check-label">Fleet</label>
                          </div>
                          <div class="mt-4 ms-2 w-50">
                            <select
                              id="subgroup-driver-id"
                              class="form-select"
                              :disabled="!isFleetChecked"
                              v-model="form.fleet_id"
                            >
                              <option :value="null">Select...</option>
                              <option
                                v-for="fleet in fleetStore.allFleets"
                                :key="fleet.id"
                                :value="fleet.id"
                              >
                                {{ fleet.name }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 其他 Tab 可依需求新增 -->
          </div>
        </div>
        <div class="modal-footer">
          <button
            class="btn btn-green btn-ok me-2"
            @click="saveChanges"
            :disabled="driverName === 'Driver name is not found'"
          >
            Save
          </button>
          <button class="btn btn-cancel" @click="closeModal">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, nextTick, watch } from 'vue';
import { useTerminalsStore } from '@/store/admin/terminal';
import { useFleetStore } from '@/store/admin/fleet';
import { Tooltip } from 'bootstrap';
import Swal from 'sweetalert2';
import { useHttp } from '@/http/http';

const props = defineProps({
  terminalId: Number,
});

const terminalsStore = useTerminalsStore();
const fleetStore = useFleetStore();

// Modal 與 Tab 狀態
const show = ref(false);
const activeTab = ref('general');

// 表單資料，新增時加入 fleet_id 欄位（預設 null）
const form = ref({
  tid: '',
  mid: '',
  serial_number: '',
  driver_id: '',
  enabled: false,
  fleet_id: null, // 預設為 null
});

// 錯誤訊息與 driver 名稱
const errors = ref({});
const driverName = ref('');

// fleet checkbox 狀態
const isFleetChecked = ref(false);

const validateForm = () => {
  errors.value = {};
  if (!form.value.tid) errors.value.tid = 'TID is required';
  if (!form.value.mid) errors.value.mid = 'MID is required';
  if (!form.value.serial_number) errors.value.serial_number = 'Serial number is required';
  // 可依需求增加其他驗證
  return Object.keys(errors.value).length === 0;
};

const resetForm = () => {
  form.value = {
    tid: '',
    mid: '',
    serial_number: '',
    driver_id: '',
    enabled: false,
    fleet_id: null,
  };
  driverName.value = '';
  isFleetChecked.value = false;
};

const showModal = async () => {
  show.value = true;
  await nextTick(); // 確保 DOM 更新完成
  await fleetStore.fetchFleetForSelect();
  initializeTooltip();
};

const closeModal = () => {
  show.value = false;
  resetForm();
};

const saveChanges = async () => {
  if (!validateForm() || driverName.value === 'Driver name is not found') {
    return;
  }
  // 如果 fleet checkbox 沒有勾選，則強制將 fleet_id 設為 null
  if (!isFleetChecked.value) {
    form.value.fleet_id = null;
  }
  try {
    const res = await terminalsStore.createTerminal(form.value);
    if (res !== false) {
      resetForm();
      closeModal();
    }
  } catch (error) {
    console.error('Error creating terminal:', error);
  }
};

// 根據 driver_id 查詢 driver 名稱
const fetchDriverInfo = async () => {
  try {
    const response = await useHttp().get(`api/admin/driver/info?driver_id=${form.value.driver_id}`);
    const driver = response.data.result;
    if (driver) {
      driverName.value = `${driver.first_name} ${driver.middle_name} ${driver.last_name}`;
    } else {
      driverName.value = 'Driver name is not found';
    }
  } catch (error) {
    driverName.value = 'Driver name is not found';
    console.error('Error fetching driver:', error);
  }
};

// 當 driver_id 輸入時清空 driver 名稱
const clearDriverName = () => {
  driverName.value = '';
};

// 初始化 Tooltip
const initializeTooltip = () => {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  tooltipTriggerList.forEach(tooltipTriggerEl => {
    new Tooltip(tooltipTriggerEl);
  });
};

onMounted(() => {
  initializeTooltip();
});
</script>

<style scoped>
.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 203.83px;
  height: 46px;
  border-radius: 30px;
}

.btn-green:hover {
  background: #00b452;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #e6e9ec;
  border-radius: 10px;
  width: 100%;
  max-width: 1200px;
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
}

.background-white {
  background-color: white;
  padding: 10px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.modal-body {
  border-radius: 10px;
  flex: 1;
  overflow-x: auto;
}

.tab-content {
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

.modal-body .row {
  display: flex;
  align-items: start;
}

.card {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 400px;
}

.card-body {
  flex-grow: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
}

label {
  margin-top: 5px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.nav-link.active {
  background-color: #e6e9ec;
  color: black;
}

.card {
  background-color: #e6e9ec;
  margin-top: 10px;
}

.btn-green:hover {
  background-color: #00b452;
}

.btn-ok {
  width: 150px;
}

.btn-cancel {
  background-color: #fff;
  color: #8476f4;
  border: 2px solid #8476f4;
  border-radius: 30px;
  width: 150px;
  height: 46px;
  padding: 14px 32px;
}

.btn-cancel:hover {
  background-color: #e6e6ff;
}
</style>
